import { Injectable, signal } from '@angular/core';
import { MenuEntry, Menus, MenusCategory } from '@smartcharge/shared/index';
import { BehaviorSubject, Subject } from 'rxjs';
import { MenuCategory, MenuCompany, MenuItem } from './types';
import { menuCategories, menuItems } from './menu-items';

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  readonly companies = signal<MenuCompany[]>([]);
  readonly menuItems = signal<MenuCategory[]>([]);

  readonly menus$ = new BehaviorSubject<Menus | null>(null);

  constructor() {
    this.menus$.subscribe((menus) => {
      if (menus) {
        localStorage.setItem('menus', JSON.stringify(menus));
        this.initializeMenus(menus);
      }
    });

    if (this.isCached()) {
      this.menus$.next(JSON.parse(localStorage.getItem('menus')));
    }
  }

  clearMenu(): void {
    localStorage.removeItem('menus');
    this.companies.set([]);
    this.menuItems.set([]);
  }

  isCached(): boolean {
    return localStorage.getItem('menus') !== null;
  }

  refreshMenus(menus: Menus): void {
    this.menus$.next(menus);
  }

  private composeMenuCategory(menuCategory: MenusCategory): MenuCategory | null {
    const category = menuCategories[menuCategory.Type];

    if (!category) {
      return null;
    }

    return {
      ...category,
      items: menuCategory.Menus.map((menu) => this.composeMenuItems(menu)).filter(Boolean),
      type: menuCategory.Type
    };
  }

  private composeMenuItems(menuItem: MenuEntry): MenuItem | null {
    const items = menuItems[menuItem.Type];

    if (!items) {
      return null;
    }

    return {
      ...items,
      type: menuItem.Type
    };
  }

  private initializeMenus(menus: Menus): void {
    const menuItems = menus.Categories.map((category) => this.composeMenuCategory(category)).filter(
      Boolean
    ) as MenuCategory[];

    this.menuItems.set(menuItems);

    const companies = menus.Companies.filter((company) => company.Menus.length).map((company) => ({
      ...company,
      Menus: company.Menus.map((category) => this.composeMenuItems(category)).filter(Boolean)
    })) as MenuCompany[];

    this.companies.set(companies);
  }
}
